import {
  Box,
  Button,
  Container,
  Flex,
  Text,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { MantineReactTable } from "mantine-react-table";
import moment from "moment";
import { useState, useMemo } from "react";
import AppBreadcrumb from "components/AppBreadcrumb";
import { report_api } from "./report.service";
import { showErrorToast, showSuccessToast } from "utilities/Toast";

export default function MaintenanceReport() {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

const fetchReport = async () => {
  // ✅ From Date மட்டும் check
  if (!fromDate) {
    showErrorToast({
      title: "Date Missing",
      message: "Please select From Date",
    });
    return;
  }

  setLoading(true);
  setTableData([]);

  // ✅ To Date இல்லனா same date use
  const startDate = moment(fromDate).format("YYYY-MM-DD");
  const endDate = toDate
    ? moment(toDate).format("YYYY-MM-DD")
    : startDate;

  const response = await report_api.get_maintenance_report(startDate, endDate);

  if (response.success) {

    if (response.data.length === 0) {
      showErrorToast({
        title: "No Records",
        message: "No maintenance records found",
      });
    }

    setTableData(response.data || []);
  }

  setLoading(false);
};


const columns = useMemo(
  () => [
    // 1️⃣ SERIAL NUMBER
    {
      header: "S.No",
      accessorFn: (_row, index) => index + 1,
      size: 50,
    },

    // 2️⃣ DATE
    {
      header: "Date",
      accessorFn: (row) =>
        moment(row.created_date).format("DD-MM-YYYY hh:mm A"),
    },

    // 3️⃣ REASON
    {
      header: "Reason",
      accessorKey: "reason",
      Cell: ({ cell }) => cell.getValue() || "-",
    },

    // 4️⃣ MAINTENANCE TYPE (Blue / Purple / Orange badge)
    {
      header: "Maintenance Type",
      accessorKey: "maintenance_type",
      Cell: ({ cell }) => {
        const type = cell.getValue();

        const typeStyles = {
         website: {
            bg: "rgb(255 235 197)",     // turquoise background
            color: "#8B4513",  // brown text
          },
        app: {
            bg: "#f3e8ff",      // light purple bg
            color: "#6f42c1",   // purple text
          },
          both: {
            bg: "#ffbfe0a6",      // light orange bg
            color: "rgb(232 81 185)",   // dark orange text
          },
        };

        const { bg, color } = typeStyles[type] || {
          bg: "#f0f0f0",
          color: "#555",
        };

        return (
          <span
            style={{
              backgroundColor: bg,
              color: color,
              padding: "4px 10px",
              borderRadius: "999px",
              fontWeight: 600,
              fontSize: "11px",
              textTransform: "uppercase",
              letterSpacing: ".5px",
            }}
          >
            {type}
          </span>
        );
      },
    },

    // 5️⃣ STATUS (Green = Running, Red = Stopped)
    {
      header: "Status",
      accessorKey: "site_status",
      Cell: ({ cell }) => {
        const val = cell.getValue();

        const isRunning = val === "running";

        return (
          <span
            style={{
              backgroundColor: isRunning ? "#d4f7d4" : "#ffe6e6",  // light green / light red bg
              color: isRunning ? "#008000" : "#cc0000",            // dark green / dark red text
              padding: "4px 10px",
              borderRadius: "999px",
              fontWeight: 700,
              fontSize: "11px",
              textTransform: "uppercase",
              letterSpacing: ".5px",
            }}
          >
            {val}
          </span>
        );
      },
    },

    // 6️⃣ ETD TIME
    {
  header: "ETD Time",
  accessorKey: "etd_maintenance_time",
  Cell: ({ cell }) => {
    const raw = cell.getValue();

    // If no value → show hyphen
    if (!raw) return "-";

    let value = raw.toString().trim().toLowerCase();
    let label = "";
    let bg = "";
    let color = "";

    // 1️⃣ Minutes ("10m")
    if (value.endsWith("m")) {
      const minutes = value.replace("m", "");
      label = `${minutes} Minutes`;

      bg = "rgb(249 255 169)";      // light yellow
      color = "rgb(232 186 7)";   // dark yellow
    }

    // 2️⃣ Hours ( "1", "2", "3"... )
    else if (!isNaN(value)) {
      label = `${value} Hours`;

      bg = "#e0f0ff";      // light blue
      color = "#0077cc";   // dark blue
    }

    // 3️⃣ Anything else → fallback
    else {
      label = value;
      bg = "#f0f0f0";
      color = "#555";
    }

    return (
      <span
        style={{
          backgroundColor: bg,
          color: color,
          padding: "4px 10px",
          borderRadius: "999px",
          fontWeight: 600,
          fontSize: "11px",
          textTransform: "capitalize",
          letterSpacing: ".3px",
        }}
      >
        {label}
      </span>
    );
  },
},


    // 7️⃣ USER
    {
      header: "User",
      accessorKey: "user_name",
    },
  ],
  []
);


  return (
    <>
      <Container fluid>
        <Box p={25}>
          <Text size="lg" fw={600} mb={10}>
            Maintenance Report
          </Text>
          <AppBreadcrumb />
        </Box>
      </Container>

      {/* FILTERS */}
      <Container fluid>
        <Box p={25} mt={-20}>
          <Flex gap={10} align="end" wrap="wrap">

  <DatePickerInput
    label="From Date"
    placeholder="Select From Date"
    value={fromDate}
    onChange={setFromDate}
    clearable
    style={{ width: "200px" }}
  />

  <DatePickerInput
    label="To Date"
    placeholder="Select To Date"
    value={toDate}
    onChange={setToDate}
    disabled={!fromDate}
    clearable
    style={{ width: "200px" }}
  />

  <Button onClick={fetchReport} loading={loading} style={{ marginTop: 6 }}>
    Search
  </Button>

</Flex>
        </Box>
      </Container>

      {/* TABLE */}
      <Container fluid mt={10} mb={30}>
        <Box px={25}>
          <MantineReactTable
            columns={columns}
            data={tableData}
            initialState={{ density: "xs" }}
            enableStickyHeader
            enableGlobalFilter={false}
            enableColumnFilters={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableColumnActions={false}
            renderTopToolbarCustomActions={() => (
              <Box px={5} py={10}>
                <Text fw={600} tt="uppercase" size="sm" color="#266f31">
                  Maintenance Report List
                </Text>
              </Box>
            )}
          />
        </Box>
      </Container>
    </>
  );
}
